import { Navigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorState } from "@a2z/ui";
import { ApiError } from "@/lib/api/client";
import { loginPathAfterSessionLoss } from "@/modules/auth/storeDeactivated";
import { useTranslate } from "@/lib/i18n";
import { queryKeys } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { useStoreBinding } from "@/modules/auth/useStoreBinding";
import { landingPathFor } from "@/modules/auth/landing";
import { Screen } from "@/shared/ui";
import { SplashScreen } from "@/shared/systemScreens/SplashScreen";
import { useSplashGate } from "@/shared/systemScreens/useSplashGate";

/**
 * Where a bare `/:locale` — and the two catch-all redirects that feed it —
 * lands, per capability.
 *
 * It used to be a hardcoded `/pos`, the same trap `LoginPage` carried: a cook
 * holds `RESTAURANT_KITCHEN_VIEW` and no `RESTAURANT_POS_USE`, so the app's
 * own front door opened onto a floor grid whose every call 403s. The decision
 * itself lives in `landing.ts` so login and this route can never drift apart.
 *
 * This sits OUTSIDE `ProtectedLayout` (it is not one of its children — it is
 * the route that chooses which child to go to), so it owns the two states that
 * layout would otherwise have covered: a logged-out session goes to login, and
 * a session that could not be read at all gets a retry rather than a guess.
 *
 * **And a third: a session bound to the WRONG STORE has not been asked yet.**
 * Which surface someone belongs on is read off their permissions, and on an
 * owner's clothing shop their restaurant permissions are simply absent — so
 * this answered "no till" and sent the person who IS the cashier to the back
 * office, every login. `useStoreBinding` repairs the binding (P5.3b) and says
 * `isPending` while it does; waiting is the fix, not re-landing afterwards,
 * because a decision taken on known-wrong inputs is not one worth correcting
 * later. If the repair fails, or the account has no restaurant or several,
 * `isPending` goes false and the fallthrough sends them to `ProtectedLayout`,
 * which owns the picker, the error and the no-restaurant screen.
 */
export function LandingRedirect() {
  const t = useTranslate();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { locale = "ar" } = useParams();
  const { session: data, isLoading, error, isPending } = useStoreBinding();

  // App boot: the session and the store-binding repair are resolving. The
  // splash paints only if that outlasts a short beat (see `useSplashGate`).
  const splash = useSplashGate(isLoading || isPending);
  if (splash.show) return <SplashScreen visible={splash.visible} />;

  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <Navigate to={loginPathAfterSessionLoss(locale, error)} replace />;
  }

  if (!data) {
    // A dropped connection, not a logged-out device. Sending them to login
    // would be a lie about what went wrong, and a guessed surface could be a
    // 403 wall, so this asks to try again.
    return (
      <Screen title={t((m) => m.common.appName)}>
        <ErrorState
          message={errorMessage(error)}
          retryLabel={t((m) => m.common.retry)}
          onRetry={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.session })
          }
        />
      </Screen>
    );
  }

  return <Navigate to={landingPathFor(locale, data)} replace />;
}
